/**
 * Wenn das Vorlesen scheitert: EINE Meldung, mit Grund. (19.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin, mit zwei Bildschirmfotos um 07:16: *„Bitte, was sollen das jetzt
 * für komische Meldungen sein? Und warum wiederholen sie sich tausendmal?"*
 *
 * Sechs Zettel uebereinander, jeder mit demselben Satz „Ein Absatz liess sich
 * nicht vorlesen: …", die den Text verdeckten, den er lesen wollte. Der Grund
 * stand in keinem davon.
 *
 * ## Zwei Fehler auf einmal
 *
 * 1. **Je Absatz eine Meldung.** Fällt die Sprachausgabe aus, scheitert JEDER
 *    Absatz — und meldete sich einzeln. Ein Waechter, der sechsmal ruft, ruft
 *    nicht lauter als einer, der einmal ruft (Martin, 31.07.: „654
 *    Meldungen!"). Hier gilt es doppelt: die Zettel verdeckten den Text.
 *
 * 2. **Kein Grund.** „Ein Absatz liess sich nicht vorlesen" sagt nicht, was
 *    zu tun ist. An dem Morgen waren ALLE DREI Stimm-Dienste aus: Soniox 402
 *    (Guthaben aufgebraucht), Google TTS 401 und ElevenLabs 401 (beide
 *    Schluessel ungueltig). Das haette in einem Satz dastehen koennen.
 *
 * ⚠️ Der Meldetext stand WORTGLEICH an zwei Stellen (tts-queue.ts und
 *    narration-engine.ts) — dasselbe Doppelbesitzer-Muster, das an diesem
 *    Wochenende schon die Karten, die Listen und die Knoepfe betroffen hat.
 */

/** Laeuft gerade ein Vorlese-Vorgang, in dem schon gemeldet wurde? */
let bereitsGemeldet = false;

/**
 * Den Grund in einen Satz uebersetzen, der sagt, was los ist.
 *
 * Die rohen Gruende kommen aus der Warteschlange („tts http 502") und sagen
 * einem Menschen nichts. Was nicht uebersetzt ist, wird weggelassen — ein
 * halbverstandener Fehlercode ist schlimmer als keiner.
 */
export function grundInWorten(grund: string): string {
  const g = grund.toLowerCase();
  if (/40[13]/.test(g)) return "Der Zugang zur Sprachausgabe gilt nicht mehr.";
  if (/402/.test(g)) return "Das Guthaben der Sprachausgabe ist aufgebraucht.";
  if (/429/.test(g)) return "Die Sprachausgabe ist gerade überlastet.";
  if (/5\d\d/.test(g)) return "Die Sprachausgabe antwortet nicht.";
  if (/abbruch|abort/.test(g)) return "";
  /*
   * ⚠️ 19.09.2026, Ticket 2329 — DER EINZIGE FALL, DEN DER MENSCH SELBST
   *    BEHEBEN KANN, war bisher der stillste.
   *
   * Bei Albert scheiterte an drei Tagen jede siebte gesprochene Antwort mit
   * „null Sekunden Ton" (216 Faelle, Quote 10-15 %). Der Browser laesst das
   * Abspielen erst nach einer Nutzergeste zu. Er bekam davon nichts mit —
   * kein Ton, keine Meldung, kein Hinweis, was zu tun waere.
   */
  if (/nicht-erlaubt/.test(g)) {
    return "Dein Browser lässt den Ton erst zu, wenn du einmal tippst.";
  }
  if (/dekodieren|format|quelle/.test(g)) {
    return "Diese Tonaufnahme lässt sich hier nicht abspielen.";
  }
  if (/netz/.test(g)) return "Der Ton ist unterwegs abgerissen.";
  return "";
}

/**
 * Einen gescheiterten Absatz melden — protokollieren immer, den Menschen
 * hoechstens einmal je Vorgang stoeren.
 *
 * `neuerVorgang()` setzt die Bremse zurueck; ohne das bliebe nach dem ersten
 * Ausfall fuer immer still.
 */
export function meldeVorleseVerlust(
  text: string,
  grund: string,
  quelle: string,
): void {
  const kurz = text.trim().slice(0, 60);
  const line = `[${quelle}] Stueck NICHT vertont (${grund}): ${kurz}`;
  try {
    console.warn(line);
    void fetch("/api/debug/ptt-timing", {
      method: "POST",
      body: JSON.stringify({ line }),
      headers: { "Content-Type": "text/plain" },
      keepalive: true,
      credentials: "include",
    }).catch(() => undefined);
    if (bereitsGemeldet) return;
    bereitsGemeldet = true;
    const warum = grundInWorten(grund);
    window.dispatchEvent(
      new CustomEvent("voice-v8:toast", {
        detail: {
          message: warum
            ? `Vorlesen geht gerade nicht. ${warum}`
            : "Vorlesen geht gerade nicht.",
          kind: "info",
          ms: 8000,
        },
      }),
    );
  } catch {
    /* kein window (Tests) */
  }
}

/** Ein neuer Vorlese-Vorgang beginnt — ab jetzt darf wieder gemeldet werden. */
export function neuerVorlesevorgang(): void {
  bereitsGemeldet = false;
}

/** Nur fuer Tests. */
export function _vorleseVerlustZuruecksetzen(): void {
  bereitsGemeldet = false;
}
